import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Headphones, Users, Ticket, Music, Star, Clock, Pencil, MessageSquare, Instagram, Facebook } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useMeta, eventJsonLd } from "@/lib/seo";
import { countryName } from "@/lib/utils_gg";
import { Avatar } from "@/components/AttendeesModal";
import EventCard from "@/components/EventCard";
import FollowButton from "@/components/FollowButton";
import FollowListModal from "@/components/FollowListModal";
import MessageComposer from "@/components/MessageComposer";
import { ProfileSetlists } from "@/components/Setlist";
import { useNavigate } from "react-router-dom";

export function RoleBadges({ roles, t, size = "w-3.5 h-3.5" }) {
  return (
    <span className="inline-flex items-center gap-2" data-testid="role-badges">
      {roles?.dj && <span className="inline-flex items-center gap-1 gg-chip gg-role-dj !text-[10px]" title={t("rolePlays")}><Headphones className={size} />{t("rolePlays")}</span>}
      {roles?.organizer && <span className="inline-flex items-center gap-1 gg-chip gg-role-organizer !text-[10px]" title={t("roleOrganizes")}><Users className={size} />{t("roleOrganizes")}</span>}
      {!roles?.dj && !roles?.organizer && <span className="inline-flex items-center gap-1 gg-chip !text-[10px]" title={t("roleGoing")}><Ticket className={size} />{t("roleGoing")}</span>}
    </span>
  );
}

function CompactRow({ e, t, lang }) {
  const d = new Date(e.start_date + "T00:00:00");
  const date = d.toLocaleDateString(lang === "de" ? "de-DE" : "en-GB", { weekday: "short", day: "2-digit", month: "short" }).toUpperCase();
  return (
    <Link to={`/events/${e.slug}`} className="flex items-center gap-3 py-2.5 border-b border-foreground/30 hover:bg-muted transition-colors" data-testid={`upcoming-row-${e.event_id}`}>
      <span className="font-display text-xs w-24 shrink-0 tracking-wide">{date}</span>
      <span className="font-display text-base leading-tight truncate flex-1">{e.name}</span>
      <span className="text-xs text-muted-foreground hidden sm:inline shrink-0">{e.city}, {countryName(e.country, lang)}</span>
      <span className="shrink-0 inline-flex items-center gap-1.5 text-primary">
        {e.roles?.dj && <Headphones className="w-4 h-4" title={t("rolePlays")} />}
        {e.roles?.organizer && <Users className="w-4 h-4" title={t("roleOrganizes")} />}
        {!e.roles?.dj && !e.roles?.organizer && <Ticket className="w-4 h-4 text-muted-foreground" title={t("roleGoing")} />}
      </span>
    </Link>
  );
}

export default function UserProfile() {
  const { user_id } = useParams();
  const { t, lang, user } = useApp();
  const [p, setP] = useState(undefined);
  useMeta({ title: p ? (p.is_dj && p.dj_name ? `${p.dj_name} – DJ` : p.name) : null, description: p ? [p.is_dj && p.dj_name ? `DJ ${p.dj_name}` : p.name, p.is_organizer ? t("organizer") : null, [p.location_city, p.location_country].filter(Boolean).join(", ") || null, (p.bio || "").slice(0, 120) || null].filter(Boolean).join(" · ") + ` – ${t("seoProfileSuffix")}` : "",
    path: p ? `/users/${p.user_id}` : undefined, image: p?.picture || undefined, type: "profile", noindex: !!p && !p.is_dj && !p.is_organizer });
  const [list, setList] = useState(null);
  const [composer, setComposer] = useState(false);
  const nav = useNavigate();
  useEffect(() => {
    const load = () => api.get(`/users/${user_id}/public`).then(r => setP(r.data)).catch(() => setP(null));
    load();
    const onFocus = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", onFocus); document.addEventListener("visibilitychange", onFocus);
    return () => { window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onFocus); };
  }, [user_id]);

  if (p === undefined) return <div className="max-w-3xl mx-auto p-8 eyebrow">…</div>;
  if (p === null) return <div className="max-w-3xl mx-auto p-8 eyebrow" data-testid="user-not-found">{t("userNotFound")}</div>;

  const loc = [p.location_city, p.location_country].filter(Boolean).join(", ");
  const [next, ...rest] = p.upcoming || [];
  const h = p.history || {};
  const hist = [h.attended > 0 && t("histAttended", { n: h.attended }), h.played > 0 && t("histPlayed", { n: h.played }), h.organized > 0 && t("histOrganized", { n: h.organized })].filter(Boolean);

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-8" data-testid="user-profile">
      <div className="eyebrow text-primary">{t("publicProfile")}</div>
      <div className="mt-4 flex items-center gap-4">
        <Avatar user={p} size="w-20 h-20 text-2xl" />
        <div className="min-w-0">
          <h1 className="font-display uppercase tracking-tighter text-4xl sm:text-5xl leading-none" data-testid="user-profile-name">{p.name}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {p.is_dj && <span className="inline-flex items-center gap-1.5 gg-chip gg-role-dj !text-[10px]" data-testid="user-profile-dj"><Headphones className="w-3 h-3" />DJ · {p.dj_name}</span>}
            {p.crews?.length > 0 ? p.crews.map(c => <Link key={c.crew_id} to={`/crews/${c.crew_id}`} className="inline-flex items-center gap-1.5 gg-chip gg-role-organizer !text-[10px] hover:opacity-80" data-testid={`user-profile-crew-${c.crew_id}`}><Users className="w-3 h-3" />{t("organizer")} · {c.name}</Link>)
            : p.is_organizer && <span className="inline-flex items-center gap-1.5 gg-chip gg-role-organizer !text-[10px]" data-testid="user-profile-organizer"><Users className="w-3 h-3" />{t("organizer")}</span>}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
            {loc && <span className="flex items-center gap-1" data-testid="user-profile-location"><MapPin className="w-4 h-4" />{loc}</span>}
            {p.member_since && <span>{t("memberSince")} {p.member_since.slice(0, 4)}</span>}
          </div>
        </div>
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-3">
        {user?.user_id === p.user_id
          ? <Link to="/profile" className="gg-btn !py-2 !px-3 text-xs" data-testid="btn-edit-profile"><Pencil className="w-4 h-4" />{t("editProfile")}</Link>
          : <><FollowButton profile={p} onChange={setP} />
            {p.accepts_messages !== false && <button onClick={() => user ? setComposer(true) : nav("/auth")} className="gg-btn !py-2 !px-3 text-xs" data-testid="btn-message-user"><MessageSquare className="w-4 h-4" />{t("message")}</button>}</>}
        <div className="flex items-center gap-4 text-sm">
          <button onClick={() => setList("followers")} className="hover:text-primary transition-colors" data-testid="followers-link"><b className="font-display text-lg">{p.followers || 0}</b> {t("followersLabel")}</button>
          <button onClick={() => setList("following")} className="hover:text-primary transition-colors" data-testid="following-link"><b className="font-display text-lg">{p.following || 0}</b> {t("followingLabel")}</button>
        </div>
      </div>
      {p.bio && <p className="mt-6 text-lg leading-relaxed border-t border-foreground pt-5" data-testid="user-profile-bio">{p.bio}</p>}
      {(p.favourite_tracks?.length > 0 || p.favourite_events?.length > 0) && (
        <div className="mt-6 border-t border-foreground pt-5 grid grid-cols-2 gap-5" data-testid="user-profile-favs">
          <div><div className="eyebrow mb-2 flex items-center gap-1.5"><Music className="w-3.5 h-3.5 text-primary" />{t("favTracks")}</div>
            {p.favourite_tracks?.length > 0 ? <ol className="font-display text-base sm:text-lg leading-snug list-decimal pl-5">{p.favourite_tracks.map((x, i) => <li key={i}>{x}</li>)}</ol> : <span className="text-sm text-muted-foreground">—</span>}</div>
          <div><div className="eyebrow mb-2 flex items-center gap-1.5"><Star className="w-3.5 h-3.5 text-primary" />{t("favEvents")}</div>
            {p.favourite_events?.length > 0 ? <ol className="font-display text-base sm:text-lg leading-snug list-decimal pl-5">{p.favourite_events.map((x, i) => <li key={i}>{x}</li>)}</ol> : <span className="text-sm text-muted-foreground">—</span>}</div>
        </div>
      )}
      {(p.instagram || p.facebook) && (
        <div className="mt-6 border-t border-foreground pt-5" data-testid="user-profile-connect">
          <div className="eyebrow mb-2">{t("connect")}</div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
            {p.instagram && <a href={p.instagram} target="_blank" rel="noreferrer noopener nofollow" className="inline-flex items-center gap-1.5 hover:text-primary transition-colors" data-testid="connect-instagram"><Instagram className="w-4 h-4" />{t("instagram")}</a>}
            {p.facebook && <a href={p.facebook} target="_blank" rel="noreferrer noopener nofollow" className="inline-flex items-center gap-1.5 hover:text-primary transition-colors" data-testid="connect-facebook"><Facebook className="w-4 h-4" />{t("facebook")}</a>}
          </div>
        </div>
      )}
      {p.preferred_genres?.length > 0 && (
        <div className="mt-6 border-t border-foreground pt-5" data-testid="user-profile-genres">
          <div className="eyebrow mb-2">{t("preferredGenres")}</div>
          <div className="flex flex-wrap gap-1.5">{p.preferred_genres.map(g => <span key={g} className="gg-chip">{t(`genres.${g}`)}</span>)}</div>
        </div>
      )}
      <div className="mt-8 border-t border-foreground pt-5" data-testid="user-profile-next">
        <div className="eyebrow mb-3 flex items-center justify-between gap-2"><span>{t("nextEventOf", { name: p.name })}</span>{next && <RoleBadges roles={next.roles} t={t} />}</div>
        {next ? <EventCard event={next} /> : <p className="text-sm text-muted-foreground" data-testid="no-next-event">{t("noUpcoming")}</p>}
      </div>
      {rest.length > 0 && (
        <div className="mt-8" data-testid="user-profile-upcoming">
          <div className="eyebrow mb-1">{t("moreUpcoming")}</div>
          <div>{rest.map(e => <CompactRow key={e.event_id} e={e} t={t} lang={lang} />)}</div>
        </div>
      )}
      <div className="mt-8 border-t border-foreground pt-4 flex items-center gap-2 text-sm text-muted-foreground" data-testid="user-profile-history">
        <Clock className="w-4 h-4" /><span className="eyebrow">{t("historyLabel")}</span>
        <span data-testid="history-line">{hist.length ? hist.join(" · ") : t("noHistoryYet")}</span>
      </div>
      {p.is_dj && <ProfileSetlists userId={p.user_id} />}
      {composer && <MessageComposer toUserId={p.user_id} toName={p.name} onClose={() => setComposer(false)} />}
      {list && <FollowListModal userId={p.user_id} kind={list} onClose={() => setList(null)} />}
    </div>
  );
}
